import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Receipt, Bot, Calendar, Camera, Upload, Sparkles, CheckCircle, Loader2, Image, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryColors = {
  office: 'bg-blue-500/20 text-blue-400',
  travel: 'bg-purple-500/20 text-purple-400',
  utilities: 'bg-yellow-500/20 text-yellow-400',
  salary: 'bg-emerald-500/20 text-emerald-400',
  marketing: 'bg-pink-500/20 text-pink-400',
  equipment: 'bg-orange-500/20 text-orange-400',
  supplies: 'bg-cyan-500/20 text-cyan-400',
  services: 'bg-indigo-500/20 text-indigo-400',
  other: 'bg-gray-500/20 text-gray-400',
};

const Expenses = () => {
  const { token, loading: authLoading } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: '',
    vendor: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Receipt Scanner Function
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (JPEG, PNG, etc.)');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64Data = e.target.result.split(',')[1];
      setPreviewImage(e.target.result);
      setScanning(true);
      setScanResult(null);

      try {
        const response = await axios.post(`${API}/ai/scan-receipt`, {
          image_base64: base64Data
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setScanResult(response.data);
          toast.success('Receipt scanned successfully! Expense created automatically.');
          fetchExpenses(); // Refresh expenses list
        } else {
          toast.error('Could not extract data from receipt. Please try a clearer image.');
        }
      } catch (error) {
        console.error('Scan error:', error);
        toast.error('Failed to scan receipt. Please try again.');
      } finally {
        setScanning(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const resetScanner = () => {
    setPreviewImage(null);
    setScanResult(null);
    setScanning(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!authLoading && token) {
      fetchExpenses();
    } else if (!authLoading && !token) {
      setLoading(false);
    }
  }, [token, authLoading]);

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenses(response.data);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        category: formData.category || null
      };
      await axios.post(`${API}/expenses`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expense added successfully!');
      setDialogOpen(false);
      setFormData({
        description: '',
        amount: '',
        category: '',
        vendor: '',
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchExpenses();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add expense';
      toast.error(message);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(value || 0);
  };

  const totalExpenses = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);

  const filteredExpenses = expenses.filter(exp =>
    exp.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    exp.vendor?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white">Expenses</h1>
          <p className="text-muted-foreground">Track and categorize your business expenses</p>
        </div>
        <div className="flex gap-3">
          {/* AI Receipt Scanner Button */}
          <Dialog open={scanDialogOpen} onOpenChange={(open) => { setScanDialogOpen(open); if (!open) resetScanner(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
                <Camera className="w-4 h-4 mr-2" />
                <Sparkles className="w-3 h-3 mr-1" />
                AI Scan Receipt
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-orbit-card border-orbit-border max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-primary flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  AI Receipt Scanner
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {!previewImage ? (
                  <div 
                    className="border-2 border-dashed border-purple-500/30 rounded-xl p-8 text-center hover:border-purple-500/50 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                      <Upload className="w-8 h-8 text-purple-400" />
                    </div>
                    <p className="text-white font-medium mb-2">Upload Receipt Image</p>
                    <p className="text-sm text-muted-foreground">
                      Take a photo or upload an image of your receipt
                    </p>
                    <p className="text-xs text-purple-400 mt-3">
                      AI will automatically extract vendor, amount, date & category
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Image Preview */}
                    <div className="relative">
                      <img 
                        src={previewImage} 
                        alt="Receipt preview" 
                        className="w-full max-h-48 object-contain rounded-lg bg-black/20"
                      />
                      <button
                        onClick={resetScanner}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>

                    {/* Scanning State */}
                    {scanning && (
                      <div className="flex items-center justify-center gap-3 py-4">
                        <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                        <span className="text-purple-400">AI is analyzing your receipt...</span>
                      </div>
                    )}

                    {/* Scan Results */}
                    {scanResult && scanResult.success && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                          <span className="text-emerald-400 font-medium">Expense Created Successfully!</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-muted-foreground">Vendor</p>
                            <p className="text-white font-medium">{scanResult.vendor || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Amount</p>
                            <p className="text-white font-medium">MYR {scanResult.amount?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Date</p>
                            <p className="text-white font-medium">{scanResult.date || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Category</p>
                            <Badge className={categoryColors[scanResult.category] || categoryColors.other}>
                              {scanResult.category || 'other'}
                            </Badge>
                          </div>
                        </div>
                        {scanResult.confidence && (
                          <div className="mt-3 pt-3 border-t border-emerald-500/20">
                            <p className="text-xs text-muted-foreground">
                              AI Confidence: {Math.round(scanResult.confidence * 100)}%
                            </p>
                          </div>
                        )}
                      </motion.div>
                    )}

                    {/* Scan Another Button */}
                    {scanResult && (
                      <Button 
                        onClick={resetScanner}
                        variant="outline"
                        className="w-full"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Scan Another Receipt
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Manual Add Expense Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orbit-glow hover:opacity-90" data-testid="add-expense-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-orbit-card border-orbit-border">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Expense</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Description *</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What was this expense for?"
                  required
                  data-testid="expense-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (MYR) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="expense-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="expense-date"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="expense-category">
                    <SelectValue placeholder="Let AI categorize or select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="office">Office</SelectItem>
                    <SelectItem value="travel">Travel</SelectItem>
                    <SelectItem value="utilities">Utilities</SelectItem>
                    <SelectItem value="salary">Salary</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="supplies">Supplies</SelectItem>
                    <SelectItem value="services">Services</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Bot className="w-3 h-3" />
                  Leave empty for AI auto-categorization
                </p>
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Input
                  value={formData.vendor}
                  onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                  placeholder="Vendor/Supplier name"
                  data-testid="expense-vendor"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  data-testid="expense-notes"
                />
              </div>
              <Button type="submit" className="w-full bg-orbit-glow hover:opacity-90" data-testid="submit-expense">
                Add Expense
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-orbit-card border-orbit-border">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">Total Expenses</p>
              <p className="text-2xl font-heading font-bold text-white tabular-nums">{formatCurrency(totalExpenses)}</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-orbit-card border-orbit-border">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">Total Records</p>
              <p className="text-2xl font-heading font-bold text-white tabular-nums">{expenses.length}</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="bg-orbit-card border-orbit-border">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">AI Categorized</p>
              <p className="text-2xl font-heading font-bold text-primary tabular-nums">
                {expenses.filter(e => e.ai_categorized).length}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Search */}
      <Card className="bg-orbit-card border-orbit-border">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search expenses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-expenses"
            />
          </div>
        </CardContent>
      </Card>

      {/* Expenses List */}
      <Card className="bg-orbit-card border-orbit-border">
        <CardHeader>
          <CardTitle className="text-white">All Expenses</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filteredExpenses.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No expenses found</p>
              <Button 
                className="mt-4 bg-orbit-glow hover:opacity-90"
                onClick={() => setDialogOpen(true)}
              >
                Add Your First Expense
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredExpenses.map((expense, index) => (
                <motion.div
                  key={expense.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-4 rounded-lg bg-orbit-bg/50 hover:bg-orbit-bg/70 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{expense.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {expense.vendor && (
                          <span className="text-xs text-muted-foreground">{expense.vendor}</span>
                        )}
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {expense.date}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Badge className={categoryColors[expense.category] || categoryColors.other}>
                        {expense.category || 'other'}
                      </Badge>
                      {expense.ai_categorized && (
                        <Badge className="bg-primary/20 text-primary">
                          <Bot className="w-3 h-3 mr-1" />
                          AI
                        </Badge>
                      )}
                    </div>
                    <span className="font-mono text-lg text-white tabular-nums">
                      {formatCurrency(expense.amount)}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Expenses;
